// File: noviopus-ats-client/src/features/PageLayout/selectors.ts
import { createSelector } from '@reduxjs/toolkit';
import { initialState } from './pageLayoutSlice';
import { safeGet } from '@/utils/safeImmutable';
import { IAccount } from '@/types/account';
import { STATUS_PAUSED } from '@/Pages/Account/constants';
import { findAllValues } from '@/models/selectors';
import { parseISO } from 'date-fns';

// Safe Selector for root state
const selectPageLayoutDomain = (state: any) => state.pageLayout || initialState;

export const selectPageParams = createSelector(selectPageLayoutDomain, (state) => state.params);
export const selectPageDialogs = createSelector(selectPageLayoutDomain, (state) => state.dialogs);
export const selectDialogParams = createSelector(selectPageLayoutDomain, (state) => state.dialogParams);
export const selectIsReactivateButtonDisabled = createSelector(selectPageLayoutDomain, (state) => state.isReactivateButtonDisabled);

// --- COMPATIBILITY SELECTORS (Aliases) ---
export const currentPageParams = () => selectPageParams;
export const currentPageParam = (name: string) => (state: any) => {
    const params = state.pageLayout?.params || {};
    return params[name];
};
export const getCurrentPageDialogs = selectPageDialogs;
export const getDialogParams = selectDialogParams;
export const getIsReactivateButtonDisabled = selectIsReactivateButtonDisabled;

// Legacy: pageProps used to return the whole state (often wrapped in a function)
export const pageProps = () => selectPageLayoutDomain;

// Legacy: Query is now in location state or we use window
export const query = (state: any) => state.router?.location?.query || {};
export const queryParam = (name: string) => (state: any) => state.router?.location?.query?.[name];

// Complex Logic Selectors
export const getDialogParamsByName = (name: string) =>
  createSelector([selectPageDialogs, selectDialogParams], (dialogs, params) => {
    const index = dialogs.findIndex((d) => d === name);
    return index !== -1 ? params[index] : {};
  });

export const getDialogParamsByProps = (state: any, props: { dialogName: string }) => {
    const dialogs = state.pageLayout?.dialogs || [];
    const params = state.pageLayout?.dialogParams || [];
    const index = dialogs.findIndex((d: string) => d === props.dialogName);
    return index !== -1 ? params[index] : {};
};

export const getIsDialogOpen = (name: string) =>
  createSelector([selectPageDialogs], (dialogs) => dialogs.includes(name));

const specialRoutes = ['/signup/restore'];

export const isHeaderToastVisible = (state: any) => {
    const account = state.auth?.currentAccount || safeGet(state.auth, 'currentAccount');
    const pathname = state.route?.locationBeforeTransitions?.pathname;
    
    if (!account) return false;
    const status = (account as any).status;
    return status === STATUS_PAUSED && !specialRoutes.includes(pathname);
};

export const getPauseReasonFromAccountLog = createSelector(
  [(state: any) => state.models?.accountLogs || {}], // Assuming models slice structure
  (accountLogs: any) => {
    // Basic logic to return latest log or empty
    const logsArray = Object.values(accountLogs);
    if (!logsArray || logsArray.length === 0) return {};
    return logsArray.sort((a: any, b: any) => +parseISO(b.createdAt) - +parseISO(a.createdAt))[0] || {};
  }
);